import { Divider } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
function SidebarFooterDivider() {
  const theme = useTheme();
  return (
    <Divider
      sx={{
        mt: theme.spacing(1),
        mx: theme.spacing(1),
        background: theme.colors.alpha.trueWhite[10]
      }}
    />
  );
}
import { Box, Typography, styled, useTheme, alpha, lighten, darken } from '@mui/material';
import { IconButton } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const FooterWrapper = styled(Box)(({ theme }) => `
  width: 100%;
  background: ${theme.palette.background.paper};
  position: relative;
  min-height: 70px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`);

const FooterDividerArea = styled(Box)<{ bgcolor?: string }>(({ theme, bgcolor }) => `
  width: 100%;
  background: ${bgcolor || theme.palette.grey[200]};
  display: flex;
  align-items: center;
  justify-content: center;
`);

const FooterContentArea = styled(Box)<{ bgcolor?: string }>(({ theme, bgcolor }) => `
  width: 100%;
  background: ${bgcolor || theme.palette.background.paper};
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
`);

interface SidebarEnvFooterProps {
  dividerBgColor?: string;
  contentBgColor?: string;
  scrollRef?: React.RefObject<any>;
}

function SidebarEnvFooter({ dividerBgColor, contentBgColor, scrollRef }: SidebarEnvFooterProps) {
  const version = process.env.REACT_APP_VERSION || 'N/A';
  const buildDate = process.env.REACT_APP_BUILD_DATE || 'N/A';
  const theme = useTheme();

  // Toggle between scrolling to top and bottom
  const [nextAction, setNextAction] = useState<'up' | 'down'>('up');
  // Track if first scroll event has occurred
  const firstScrollHandled = useRef(false);
  useEffect(() => {
    setNextAction('up'); // Always up on first load
    firstScrollHandled.current = false;
  }, []);
  const isAnimatingRef = useRef(false);

  // Helper to determine where we are in the scroll container
  function updateNextActionFromPosition() {
    const scrollbars = scrollRef?.current;
    const element: HTMLElement | undefined = scrollbars?.view;
    if (!element) return;
    const { scrollTop } = element;
    const nearTop = scrollTop <= 16; // threshold
    if (!firstScrollHandled.current) {
      // On first scroll event, keep icon as 'up' regardless of position
      firstScrollHandled.current = true;
      setNextAction('up');
      return;
    }
    if (nearTop) {
      setNextAction('down');
    } else {
      setNextAction('up');
    }
  }

  // Attach scroll listener to keep icon in sync with position
  useEffect(() => {
    const scrollbars = scrollRef?.current;
    const element: HTMLElement | undefined = scrollbars?.view;
    if (!element) return;
    updateNextActionFromPosition();
    const onScroll = () => {
      if (isAnimatingRef.current) return; // ignore during animation to avoid flashing
      updateNextActionFromPosition();
    };
    element.addEventListener('scroll', onScroll, { passive: true });
    return () => element.removeEventListener('scroll', onScroll);
  }, [scrollRef?.current]);

  // Smooth scroll helper
  function smoothScrollTo(targetTop: number, duration = 400, onDone?: () => void) {
    const scrollbars = scrollRef?.current;
    const element: HTMLElement | undefined = scrollbars?.view;
    if (!element) return;
    const start = element.scrollTop;
    const change = targetTop - start;
    const startTime = performance.now();

    // easeInOutCubic
    const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    function animate(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = ease(progress);
      element.scrollTop = start + change * eased;
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        onDone && onDone();
      }
    }
    requestAnimationFrame(animate);
  }

  // Match sidebar background color
  const bgColor = theme.palette.mode === 'dark'
    ? alpha(lighten(theme.header.background, 0.1), 0.5)
    : darken(theme.colors.alpha.black[100], 0.5);
  return (
    <FooterWrapper>
      <FooterDividerArea bgcolor={bgColor}>
        <Box width="100%" px={1}>
          <Box
            sx={{
              borderTop:
                theme.palette.mode === 'dark'
                  ? `1px solid ${alpha(theme.palette.common.black, 0.35)}`
                  : `1px solid ${theme.colors.alpha.trueWhite[10]}`,
              width: '100%'
            }}
          />
        </Box>
      </FooterDividerArea>
      <FooterContentArea bgcolor={bgColor} px={2} py={1}>
        {/* Left-aligned icon and version */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <IconButton
            size="small"
            aria-label={nextAction === 'up' ? 'Scroll to top' : 'Scroll to bottom'}
            sx={{ ml: 0, mr: 3, color: theme.palette.mode === 'light' ? theme.colors.alpha.white[70] : theme.palette.text.secondary }}
            onClick={() => {
              const scrollbars = scrollRef?.current;
              const element: HTMLElement | undefined = scrollbars?.view;
              if (!element) return;
              if (isAnimatingRef.current) return; // ignore clicks during animation
              const maxScrollTop = Math.max(0, element.scrollHeight - element.clientHeight);
              if (nextAction === 'up') {
                isAnimatingRef.current = true;
                smoothScrollTo(0, 400, () => {
                  isAnimatingRef.current = false;
                  updateNextActionFromPosition();
                });
              } else {
                isAnimatingRef.current = true;
                smoothScrollTo(maxScrollTop, 400, () => {
                  isAnimatingRef.current = false;
                  updateNextActionFromPosition();
                });
              }
            }}
          >
            {nextAction === 'up' ? (
              <ArrowUpwardIcon fontSize="small" />
            ) : (
              <ArrowDownwardIcon fontSize="small" />
            )}
          </IconButton>
          <Typography
            variant="caption"
            sx={{
              ml: 0.5,
              color: theme.palette.mode === 'light' ? theme.colors.alpha.white[70] : theme.palette.text.secondary,
              fontWeight: 500
            }}
          >
            v {version}
          </Typography>
        </Box>
        {/* Build date stays right-aligned */}
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.mode === 'light' ? theme.colors.alpha.white[70] : theme.palette.text.secondary,
            fontWeight: 500
          }}
        >
          Build {buildDate}
        </Typography>
      </FooterContentArea>
    </FooterWrapper>
  );
}

export default SidebarEnvFooter;
